import {AppState, Dispatch, ExtraArgs, GetState, ThunkAction} from '../types'
import {clearEnviroments} from '../components/ResourceSelector/actions';
import {selectors as authSelectors} from './auth';
import {fetchLabels} from '../common/labels/actions';
import {Urls} from '../components/Sidebar/Urls';
import FeatureFlagService from '../services/FeatureFlagService';
import FeatureFlags from '../types/featureflags';

/**
 *  Action Type
 */
export enum LifecycleValues {
  Initializing = 'Monitoring/Lifecycle/Initializing',
  InitializeError = 'Monitoring/Lifecycle/InitializeError',
  Initialized = 'Monitoring/Lifecycle/Initialized',
  UnexpectedError = 'Monitoring/Lifecycle/UnexpectedError'
}

export enum SyncLifecycleValues {
  InitializingSync = 'Monitoring/Lifecycle/Initializing',
  InitializedSync = 'Monitoring/Lifecycle/InitializedSync',
  InitializedSyncError = 'Monitoring/Lifecycle/InitializedSyncError'
}

export type LifecycleAction = {
  type: LifecycleValues | SyncLifecycleValues | PermissionValues,
  payload?: any
}

export const actions = {
  /**
   *  Action Creators
   */
  update: (): ThunkAction<void> => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    const hasMonitoringCenter = authSelectors.getMonitoringCenterEntitlementSKU(getState()) > -1;
    const hasApiMonitoring = authSelectors.getApiMonitoringEntitlementSchedules(getState()) > 0;
    if (!hasMonitoringCenter && !hasApiMonitoring) {
      return dispatch(
          {type: LifecycleValues.InitializeError, payload: 'Forbidden - User doesn\'t have correct entitlements'}
          )
    }

    const initialized = (url: Urls) => {
      const hash = window.location.hash;
      if (!hash || hash === '#' || hash === '#/') {
        window.location.hash = url
      }
      dispatch({type: LifecycleValues.Initialized})
    };

    // if user does not have view role then display authorization required message
    const hasViewRole =  authSelectors.hasViewRole(getState());
    if (!hasViewRole) {
      dispatch(
          {type: SyncLifecycleValues.InitializedSyncError, payload: 'Not allowed - User doesn\'t have permission to access'}
      );
      return initialized(hasMonitoringCenter ? Urls.builtIn : Urls.apitesting)
    }

    // only api-testing
    if (!hasMonitoringCenter) {
      return initialized(Urls.apitesting)
    }

    // monitoring center required sync, while api monitoring doesn't
    dispatch(clearEnviroments());

    const promises = [
      FeatureFlagService.init(authSelectors.getMasterOrganizationId(getState()),
                              authSelectors.getMasterOrganizationName(getState())),
      api.ui(getState).sync()
    ];

    Promise.all(promises).then(() => {
      const isDeferLoadingFlagEnabled = FeatureFlagService.get(FeatureFlags.DEFER_LABELS_CALL_IN_LANDING_PAGE_ENABLED);

      if (!isDeferLoadingFlagEnabled) {
        dispatch(fetchLabels('monitoring'))
      }
      dispatch({type: SyncLifecycleValues.InitializedSync});
      initialized(Urls.builtIn)
    }).catch((error) => {
      let detail = '';
      if (error) {
        if (typeof error === 'string') detail = error;
        if (typeof error.message === 'string') detail = error.message;
        if (typeof error.errorBody === 'string') detail += ` - ${error.errorBody}`;
      }
      dispatch({type: SyncLifecycleValues.InitializedSyncError, payload: detail});
      console.error('Sync error:', error);
      initialized(hasApiMonitoring ? Urls.apitesting : Urls.builtIn)
    })
  },
  unexpectedError: (error: any): ThunkAction<void> => (dispatch: Dispatch) => {
    dispatch({ type: LifecycleValues.UnexpectedError, payload: error})
  },
    // new checkPermissions action
  checkPermissions: (): ThunkAction<void> => (dispatch: Dispatch, getState: GetState) => {
    const hasMonitoringCenter = authSelectors.getMonitoringCenterEntitlementSKU(getState()) > -1;
    const hasApiMonitoring = authSelectors.getApiMonitoringEntitlementSchedules(getState()) > 0;
    const hasViewRole = authSelectors.hasViewRole(getState());
    
    dispatch({
      type: PermissionValues.PermissionsChecked,
      payload: {
        hasMonitoringCenter,
        hasApiMonitoring,
        hasViewRole
      }
    });
  }
};

/**
 *   Model
 */

export enum PermissionValues {
  PermissionsChecked = 'Monitoring/Lifecycle/PermissionsChecked'
}

export type LifecycleState = {
  state: LifecycleValues,
  error: string,
  syncState: SyncLifecycleValues,
  syncError: string,
  permissionsChecked: boolean,
  hasMonitoringCenter: boolean,
  hasApiMonitoring: boolean,
  hasViewRole: boolean
}

const initalState: LifecycleState = {
  state: LifecycleValues.Initializing,
  error: '',
  syncState: SyncLifecycleValues.InitializingSync,
  syncError: '',
  permissionsChecked: false,
  hasMonitoringCenter: false,
  hasApiMonitoring: false,
  hasViewRole: false
};

/**
 *   Reducer
 */
export const reducer = (state = initalState, action: LifecycleAction): LifecycleState => {
  switch (action.type) {
    case LifecycleValues.Initializing:
    case LifecycleValues.Initialized:
      return {
        ...state,
        state: action.type,
        error: ''
      };

    case LifecycleValues.InitializeError:
      return {
        ...state,
        state: action.type,
        error: action.payload || ''
      };

    case LifecycleValues.UnexpectedError:
      if (action.payload.shouldStopIframe) {
        return {
          ...state,
          state: action.type,
          error: action.payload.message || ''
        }
      } else {
        return state;
      }

    case SyncLifecycleValues.InitializingSync:
    case SyncLifecycleValues.InitializedSync:
      return {
        ...state,
        syncState: action.type,
        syncError: ''
      };

    case SyncLifecycleValues.InitializedSyncError:
      return {
        ...state,
        syncState: action.type,
        syncError: action.payload || ''
      };
    
    case PermissionValues.PermissionsChecked:
      return {
        ...state,
        permissionsChecked: true,
        hasMonitoringCenter: action.payload.hasMonitoringCenter,
        hasApiMonitoring: action.payload.hasApiMonitoring,
        hasViewRole: action.payload.hasViewRole
      };

    default:
      return state
  }
};

/**
 * Selectors
 */
export const selectors = {
  getAll: (state: AppState) => state.monitoring.lifecycle,
  getInitializationState: (state: AppState) => selectors.getAll(state).state,
  getInitializationError: (state: AppState) => selectors.getAll(state).error,
  getSyncInitializationState: (state: AppState) => selectors.getAll(state).syncState,
  getSyncInitializationError: (state: AppState) => selectors.getAll(state).syncError,

  getPermissionsChecked: (state: AppState) => selectors.getAll(state).permissionsChecked,
  getHasMonitoringCenter: (state: AppState) => selectors.getAll(state).hasMonitoringCenter,
  getHasApiMonitoring: (state: AppState) => selectors.getAll(state).hasApiMonitoring,
  getHasViewRole: (state: AppState) => selectors.getAll(state).hasViewRole
};
